/**
 * 개인정보처리방침 페이지 (호주 Privacy Act 1988 준수)
 */

import Seo from "@/components/seo/Seo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Lock, Eye, Trash2, Mail } from "lucide-react";

export default function Privacy() {
  return (
    <>
      <Seo
        title="개인정보처리방침 — OZK News"
        description="OZK News 개인정보처리방침 (Privacy Policy)"
        path="/privacy"
      />

      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">개인정보처리방침</h1>
            <p className="text-lg text-muted-foreground">
              Privacy Policy (Privacy Act 1988 준수)
            </p>
          </div>

          <Alert className="mb-6">
            <Shield className="h-4 w-4" />
            <AlertDescription>
              OZK News는 호주 개인정보 보호법(Privacy Act 1988)을 준수하며, 이용자의 개인정보를 안전하게 보호하고 책임 있게 관리합니다.
            </AlertDescription>
          </Alert>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  수집하는 개인정보
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">필수 정보</h3>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>이메일 주소 (회원가입 시)</li>
                    <li>닉네임 또는 이름 (서비스 이용 시)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">선택 정보</h3>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>전화번호 (거래 시)</li>
                    <li>주소 (위치 기반 서비스 이용 시)</li>
                    <li>회사명 (광고/제휴 문의 시)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>개인정보 수집 및 이용 목적</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>서비스 제공 및 계약 이행</li>
                  <li>회원 관리 및 본인 확인</li>
                  <li>문의사항 응답 및 고객 지원</li>
                  <li>마케팅 및 광고 상담 (동의 시)</li>
                  <li>서비스 개선 및 통계 분석</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  개인정보 보유 및 이용 기간
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  개인정보는 수집 목적이 달성된 후 즉시 파기됩니다. 단, 다음의 경우 일정 기간 보관됩니다:
                </p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground mt-2">
                  <li>회원 정보: 회원 탈퇴 시까지</li>
                  <li>거래 정보: 거래 완료 후 3개월</li>
                  <li>문의 정보: 문의 처리 완료 후 3개월</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>제3자 제공</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  이용자의 사전 동의 없이 제3자에게 개인정보를 제공하지 않습니다. 단, 다음의 경우는 예외입니다:
                </p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground mt-2">
                  <li>법령에 의한 요구가 있는 경우</li>
                  <li>서비스 제공을 위해 필요한 최소한의 범위 내에서 제3자에게 제공 (예: 결제 서비스)</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trash2 className="w-5 h-5" />
                  이용자의 권리
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Privacy Act 1988에 따라 이용자는 다음 권리를 가집니다:
                </p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>개인정보 열람 요청</li>
                  <li>개인정보 수정 요청</li>
                  <li>개인정보 삭제 요청</li>
                  <li>개인정보 처리 정지 요청</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-4">
                  권리 행사는 <a href="mailto:ozknews.com@gmail.com" className="text-brand-primary hover:underline">ozknews.com@gmail.com</a>으로 요청하실 수 있습니다.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>보안 관리</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>개인정보 암호화 저장 및 전송</li>
                  <li>서버 접근 제한 및 보안 점검</li>
                  <li>이메일 인증을 통한 계정 보호</li>
                  <li>정기적인 보안 업데이트</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  문의
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  개인정보 처리와 관련한 문의사항이 있으시면 아래로 연락 주세요:
                </p>
                <p className="mt-2">
                  <a
                    href="mailto:ozknews.com@gmail.com"
                    className="text-brand-primary hover:underline"
                  >
                    ozknews.com@gmail.com
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
